const TEXT = new TextEncoder();
const TEXT_DECODER = new TextDecoder();
// Chosen on 2026-03-23 for a fast-enough local UX baseline in Spürhund.
// OWASP's newer SHA-256 guidance is higher, so this should be revisited if the
// threat model changes or if we add stronger long-term secret storage guarantees.
const PBKDF2_ITERATIONS = 250_000;

export interface EncryptedBlob {
  ciphertext: string;
  iv: string;
}

export interface SecretExportEnvelope extends EncryptedBlob {
  salt: string;
}

export async function createSharedKey(): Promise<string> {
  return bytesToBase64Url(crypto.getRandomValues(new Uint8Array(32)));
}

export async function encryptSharedPayload(payload: string, key: string): Promise<string> {
  const cryptoKey = await importSharedKey(key);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    cryptoKey,
    TEXT.encode(payload),
  );
  return JSON.stringify({
    v: 1,
    iv: bytesToBase64Url(iv),
    ct: bytesToBase64Url(new Uint8Array(ciphertext)),
  });
}

export async function decryptSharedPayload(blob: string, key: string): Promise<string> {
  const parsed = JSON.parse(blob) as { v: number; iv: string; ct: string };
  if (parsed.v !== 1) throw new Error("Unsupported encrypted payload version");
  const cryptoKey = await importSharedKey(key);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(base64UrlToBytes(parsed.iv)) },
    cryptoKey,
    toArrayBuffer(base64UrlToBytes(parsed.ct)),
  );
  return TEXT_DECODER.decode(plaintext);
}

export async function exportSecretsEncrypted(
  plaintext: string,
  password: string,
): Promise<SecretExportEnvelope> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await derivePasswordKey(password, salt);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: toArrayBuffer(iv) },
    key,
    TEXT.encode(plaintext),
  );
  return {
    ciphertext: bytesToBase64Url(new Uint8Array(ciphertext)),
    iv: bytesToBase64Url(iv),
    salt: bytesToBase64Url(salt),
  };
}

export async function importSecretsEncrypted(
  blob: SecretExportEnvelope,
  password: string,
): Promise<string> {
  const key = await derivePasswordKey(password, base64UrlToBytes(blob.salt));
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toArrayBuffer(base64UrlToBytes(blob.iv)) },
    key,
    toArrayBuffer(base64UrlToBytes(blob.ciphertext)),
  );
  return TEXT_DECODER.decode(plaintext);
}

async function derivePasswordKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    TEXT.encode(password),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function importSharedKey(key: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    toArrayBuffer(base64UrlToBytes(key)),
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  const base64 = btoa(binary);
  return base64.replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function base64UrlToBytes(base64url: string): Uint8Array {
  const padded = `${base64url}${"=".repeat((4 - (base64url.length % 4 || 4)) % 4)}`
    .replaceAll("-", "+")
    .replaceAll("_", "/");
  const binary = atob(padded);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}
